import { FieldErrors } from 'react-hook-form';
import {
  Pagination,
  PermissionEnum,
} from '../../../../../baseella-ui-components/src';
import { CustomerStatus, OrganizationType } from '../types';

export enum ApplicationType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
  MAIN_REPRESENTATIVE = 'MAIN_REPRESENTATIVE',
}

export interface AllowedCategory {
  id: string;
  name: string;
  required: boolean;
  category?: string;
}

export enum AgreementType {
  GENERAL_T_AND_CS = 'GENERAL_T_AND_CS',
  PRIVACY_NOTICE = 'PRIVACY_NOTICE',
  ANNEX_1 = 'ANNEX_1',
  ANNEX_2 = 'ANNEX_2',
  ANNEX_3 = 'ANNEX_3',
  ANNEX_4 = 'ANNEX_4',
  ANNEX_5 = 'ANNEX_5',
  ANNEX_6 = 'ANNEX_6',
  ANNEX_7 = 'ANNEX_7',
  ANNEX_8 = 'ANNEX_8',
}

export enum DocumentType {
  IDENTIFICATION_DOCUMENTS = 'identificationDocuments',
  TRANSACTION_DOCUMENTS = 'transactionDocuments',
  PSP_CUSTOMER_DOCUMENTS = 'pspCustomerDocuments',
  CDD_CHECK_DOCUMENTS = 'cddCheckDocuments',
  OTHER_DOCUMENTS = 'otherDocuments',
}

export interface CustomerAgreement {
  archivedAt: string;
  client: OrganizationType;
  createdAt: string;
  description: string;
  id: string;
  signatures: {
    edges: { node: { id: string; signedAt: string } }[];
  };
  startAt: string;
  type: AgreementType;
  uploadId: string;
  version: number;
  earlyVersions?: {
    archivedAt: string;
    client: OrganizationType;
    createdAt: string;
    description: string;
    id: string;
    signatures: {
      edges: { node: { id: string; signedAt: string } }[];
    };
    startAt: string;
    type: AgreementType;
    uploadId: string;
    version: number;
  }[];
  render?: Record<string, React.JSX.Element>;
}

export type CustomerAgreementDictionary<T> = Record<string, T[]>;

export enum CustomerDocumentStatus {
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export enum CustomerDocumentType {
  APPLICATION_INCORPORATION_CERTIFICATE = 'APPLICATION_INCORPORATION_CERTIFICATE',
  APPLICATION_ARTICLES_OF_ASSOCIATIONS = 'APPLICATION_ARTICLES_OF_ASSOCIATIONS',
  APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS = 'APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS',
  APPLICATION_REGISTRY_OF_THE_DIRECTORS = 'APPLICATION_REGISTRY_OF_THE_DIRECTORS',
  APPLICATION_ANNUAL_ACCOUNTS = 'APPLICATION_ANNUAL_ACCOUNTS',
  CLIENT_IDENTIFICATION = 'CLIENT_IDENTIFICATION',
  CLIENT_TRANSACTION = 'CLIENT_TRANSACTION',
  CLIENT_PSP_CUSTOMER = 'CLIENT_PSP_CUSTOMER',
  CLIENT_CDD_CHECK = 'CLIENT_CDD_CHECK',
  OTHER = 'OTHER',
}

export interface DocumentComment {
  createdAt: string;
  deletedAt: string;
  documentId: string;
  id: string;
  text: string;
  updatedAt: string;
  userId: string;
  user: {
    profile: {
      firstName: string;
      lastName: string;
    };
  };
}

export interface CustomerDocument {
  name: string;
  uploadId: string;
  category?: CustomerDocumentType;
  expirationDate: string;
  status?: CustomerDocumentStatus;
  userId: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  isSelected?: boolean;
  commentsList?: DocumentComment[];
  documentCategoryId?: string;
  prevDocumentCategoryId?: string;
  transactionId?: string;
  url?: string;
  upload?: {
    mimeType?: string;
  };
  countComments?: {
    count: number;
  };
  comments?: {
    edges: {
      node: DocumentComment;
    }[];
  };
  warning?: boolean;
  warningMessage?: string;
  render?: Record<string, React.JSX.Element>;
}

export enum DocumentStatus {
  ACCEPTED = 'Accepted',
  NEW = 'New',
  EXPIRED = 'Expired',
  REJECTED = 'Rejected',
}

export interface Document {
  id: string;
  name: string;
  date: string;
  status: DocumentStatus;
  comments: number;
  commentsList?: DocumentComment[];
  link: string;
  warning?: boolean;
  isSelected?: boolean;
  documentType?: string;
  type: 'image' | 'file';
}

export enum DocumentErrorType {
  EXPIRED = 'expired',
  EXPIRED_SOON = 'expiredSoon',
}

export interface DocumentInfo {
  id: string;
  link: string;
  group: DocumentType;
  status: DocumentStatus;
  expiredDate: string;
  name: string;
  loadedDate: string;
  userName: string;
  error?: {
    type: DocumentErrorType;
    title: string;
    description?: string;
  };
  message?: string;
  comments?: DocumentComment[];
}

export interface DocumentsCategoryState {
  startCursor: string;
  endCursor: string;
  hasNext: boolean;
  hasPrev: boolean;
  prevCursor?: string;
  prevStartCursor?: string;
  prevEndCursor?: string;
  documents?: CustomerDocument[];
}

export type DocumentsState = Record<string, DocumentsCategoryState[]>;

export type CursorByCategoryId = Record<
  string,
  {
    startCursor?: string;
    endCursor?: string;
    take?: string;
  }
>;

export interface Cursor {
  startCursor: string;
  endCursor: string;
  take: string;
}

export interface AgreementsTypes {
  customerId?: string;
  type: OrganizationType;
}

export interface ExpiredDocumentsTypes {
  type: OrganizationType;
  customerId?: string;
  activePermissions?: PermissionEnum[];
  setDeletePopupVisible: (data: boolean) => void;
  deleteDocumentIsSuccess?: boolean;
  setSubMenuVisible: (data: boolean) => void;
  setSelectedSubMenuDocumentId: (data: string) => void;
  setRenamePopupVisible: (data: boolean) => void;
  setSelectedDocumentToRename: (data?: CustomerDocument) => void;
  getFile: (data: CustomerDocument) => void;
  subMenuVisible?: boolean;
  selectedSubMenuDocumentId?: string;
  setExpiredDocumentsVisible: (data: boolean) => void;
}

export interface DocumentsPopupsTypes {
  selectedCategory?: {
    id: string;
    category: string;
  };
  allDocumentsSelected: number;
  selectedDocumentsCountByCategory: number;
  setDeletePopupVisible: (data: boolean) => void;
  documentsByCategoryId?: DocumentsState;
  allowedCategories?: AllowedCategory[];
  allDocuments?: DocumentsState;
  onDelete: (data: string[]) => void;
  deleteInProgress?: boolean;
  deletePopupVisible: boolean;
  setRenamePopupVisible: (data: boolean) => void;
  setSelectedDocumentToRename: (data?: CustomerDocument) => void;
  setDocumentName: (data: string) => void;
  setNameError: (data: string) => void;
  onRename: () => void;
  documentRenameInProgress?: boolean;
  renamePopupVisible: boolean;
  documentName?: string;
  selectedDocumentToRename?: CustomerDocument;
  nameError?: string;
  errors?: FieldErrors<any>;
}

export interface DocumentsTopTypes {
  expiredDocumentsPagination?: Pagination;
  expiredDocuments?: CustomerDocument[];
  setExpiredDocumentsVisible: (data: boolean) => void;
  type: OrganizationType;
  activePermissions?: PermissionEnum[];
  organizationStatus?: CustomerStatus;
  customerId?: string;
  search?: string;
  allDocumentsSelected: number;
  selectedDocumentsCountByCategory: number;
  menuVisible?: boolean;
  setMenuVisible: (data: boolean) => void;
  moveInProgress?: boolean;
  selectedCategory?: {
    id: string;
    category: string;
  };
  allowedCategories?: AllowedCategory[];
  documentsByCategoryId?: DocumentsState;
  allDocuments?: DocumentsState;
  changeCustomerDocumentsCategoryAction: (
    documentCategoryId: string,
    selectedIds: string[],
  ) => void;
  otherActionsMenuVisible?: boolean;
  setOtherActionsMenuVisible: (data: boolean) => void;
  statusChangeInProgress?: boolean;
  setSelectedStatus: (data: CustomerDocumentStatus) => void;
  changeCustomerDocumentsStatusAction: (
    status: CustomerDocumentStatus,
    selectedIds: string[],
  ) => void;
  selectedStatus?: CustomerDocumentStatus;
  setDeletePopupVisible: (data: boolean) => void;
  getFiles: (selectedDocuments: CustomerDocument[]) => void;
  downloadInProgress?: boolean;
  isFetching?: boolean;
  searchText?: string;
  activeLabel?: boolean;
  onSearch: (data: string) => void;
  setActiveLabel: (data: boolean) => void;
}

export interface AgreementTypes {
  customerType: OrganizationType | ApplicationType;
  isApplication?: boolean;
}
